import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import cookie from 'js-cookie';
import { replaceURLPlaceholders } from '@yakit/ui-components';

import { VDLDefaultIframe } from '@VDL/components/VDLDefaultIframe/VDLDefaultIframe';
import { ObjectTreeWrapper } from '@/components/ObjectTreeWrapper';
import { VDLBanner } from '@VDL/components/VDLBanner/VDLBanner';
import { VDLToolbar } from '@VDL/components/VDLToolbar/VDLToolbar';
import { VDLSidebar } from '@VDL/components/VDLSidebar/VDLSidebar';
import { VDLContainer } from '@VDL/components/VDLContainer';
import { HeaderMFWrapper } from '@/components/HeaderMFWrapper';

import { useVDLStore } from '@VDL/store/VDLstore';
import { useVDLModalStore } from '@VDL/store/VDLModalStore';

import { getButtonLog } from '@VDL/api';
import { useUpdateURLWithPattern } from '@VDL/hooks/useUpdateURLWithPattern';
import useHashGenerator from '@VDL/hooks/useHashGenerator';
import { useFetchAgreement } from '@VDL/hooks/useFetchAgreement';

import { INITIAL_PAGE_STATE } from '@VDL/utils/consts';

import type { TVDLLayoutState } from '@VDL/types/VDLLayoutState';
import type { IVDLButton } from '@VDL/api/types/IVDLButton';

export const Level0View = () => {
  const { level0Code } = useParams();
  const { selectedYear } = useVDLStore();
  const { setDefaultIframe } = useVDLModalStore();
  const [token, setToken] = useState<string | undefined>(undefined);
  const [pageState, setPageState] =
    useState<TVDLLayoutState>(INITIAL_PAGE_STATE);

  useFetchAgreement();

  useEffect(() => {
    const token = cookie.get('token');
    setToken(token);
  }, []);

  const { updatedURL } = useUpdateURLWithPattern({
    unresolvedURL: pageState.unresolvedURL,
    level0Code,
    selectedYear
  });

  const { isHashLoading } = useHashGenerator(token);

  const onClickButtonObject = (buttonObject: IVDLButton) => {
    const type = buttonObject.tip;
    const unresolvedURL = buttonObject.parms;
    const resolvedURL = replaceURLPlaceholders(unresolvedURL ?? '', {
      lvl_0: level0Code,
      year: selectedYear
    });

    let newState: TVDLLayoutState | undefined;

    getButtonLog(buttonObject.code);

    switch (type) {
      case 'modal':
        setDefaultIframe({
          type: 'default',
          title: buttonObject.name ?? '',
          src: resolvedURL
        });
        newState = INITIAL_PAGE_STATE;
        break;
      case 'iframe':
        newState = {
          code: buttonObject.code,
          type: 'iframe',
          title: buttonObject.name ?? '',
          resolvedURL,
          unresolvedURL: unresolvedURL ?? '',
          isEdit: null,
          haveBackButton: true
        };
        break;
      case 'callback':
        newState = {
          code: buttonObject.code,
          type: 'event_plan',
          title: 'План мероприятий',
          resolvedURL: null,
          unresolvedURL: null,
          isEdit: false,
          haveBackButton: true
        };
        break;
      case 'link':
        window.open(resolvedURL, '_blank');
        break;
      case 'request':
        if (resolvedURL) {
          const a = document.createElement('a');
          a.href = resolvedURL;
          a.download = '';
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        newState = INITIAL_PAGE_STATE;
        break;
    }

    setPageState((prev) => {
      if (!newState) return INITIAL_PAGE_STATE;
      if (prev.code === newState.code) return INITIAL_PAGE_STATE;
      return newState;
    });
  };

  if (!selectedYear || !level0Code || isHashLoading) return;

  return (
    <div className="relative flex min-h-dvh flex-col">
      <HeaderMFWrapper />
      <VDLContainer className="shrink-0 md:mt-4">
        <VDLBanner
          title="Показатели оценки эффективности деятельности высших должностных лиц"
          onClickButtonObject={onClickButtonObject}
          activeButtonObjectCode={pageState.code}
          loadingButton=''
        />
      </VDLContainer>
      <div className="relative min-h-[37.5rem] flex-1">
        <div className="absolute inset-0 h-full w-full">
          <VDLContainer className="relative flex h-full w-full flex-col gap-6 py-6 pr-6 pl-6 md:pl-[calc(2rem_+_1.75rem_+_3.125rem)]">
            <VDLToolbar />
            <div className="absolute top-0 left-6 h-full flex-1">
              <VDLSidebar />
            </div>
            <div className="relative flex flex-1 flex-col">
              <div className="relative h-full w-full">
                {pageState.type === 'default' && (
                  <ObjectTreeWrapper
                    queryKey={`tree/vdlknpdata?guid=e427a7ca17a19067&year=${selectedYear}&key=${level0Code}&tid=vdlknpdata&is_edit=1`}
                    saveKey="vdl/vdlknp/btn/save"
                    baseURL={
                      import.meta.env.DEV
                        ? `/dev-proxy${import.meta.env.VITE_OBJECT_TREE_API_URL}`
                        : import.meta.env.VITE_OBJECT_TREE_API_URL
                    }
                    tokenKey={import.meta.env.VITE_TOKEN_KEY}
                    apiKey={
                      import.meta.env.DEV
                        ? import.meta.env.VITE_API_KEY
                        : undefined
                    }
                    rounded="calc(0.625rem + 4px)"
                  />
                )}
                {pageState.type === 'event_plan' && (
                  <ObjectTreeWrapper
                    queryKey={`tree/vdlknp?year=${selectedYear}&key=${level0Code}&is_edit=1&tid=vdlknp`}
                    saveKey="vdl/vdlknp/btn/save"
                    baseURL={
                      import.meta.env.DEV
                        ? `dev-proxy${import.meta.env.VITE_OBJECT_TREE_API_URL}`
                        : import.meta.env.VITE_OBJECT_TREE_API_URL
                    }
                    tokenKey={import.meta.env.VITE_TOKEN_KEY}
                    apiKey={
                      import.meta.env.DEV
                        ? import.meta.env.VITE_API_KEY
                        : undefined
                    }
                    rounded="calc(0.625rem + 4px)"
                  />
                )}
                {pageState.type === 'iframe' && (
                  <VDLDefaultIframe src={updatedURL ?? pageState.resolvedURL} />
                )}
              </div>
            </div>
          </VDLContainer>
        </div>
      </div>
    </div>
  );
};
